import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
export function CookiePolicyPage() {
  usePageMeta('Cookie Policy | findonlinecollege.com', 'Learn how findonlinecollege.com uses cookies and similar tracking technologies.');
  return <main className="bg-white py-10 sm:py-16" data-id="element-1240" data-mp="1fdu1kg7jxzsg">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-1241" data-mp="1kbm567hk7ach">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-1242" data-mp="1kdq74jvwdza4">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-1243" data-mp="1uauwesl1iav5" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-1244" data-mp="1yac2no8ajc1d">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-1245" data-mp="3w1y9rpmbe3je">
            Cookie Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3" data-id="element-1246" data-mp="3z6rlyuvjxc0">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </header>

        {/* Intro */}
        <div className="space-y-5 leading-relaxed text-base" data-id="element-1247" data-mp="31nvy7ia2x7mv">
          <p data-id="element-1248" data-mp="2wpkm10wbal1h">
            This Cookie Policy explains how findonlinecollege.com ("we", "us",
            or "our") uses cookies and similar tracking technologies when you
            visit our website. It explains what these technologies are, why we
            use them, and your rights to control our use of them.
          </p>
        </div>

        {/* Section 1 */}
        <NumberedSection id="section-1" number={1} title="What Are Cookies?" data-id="element-1249" data-mp="3ff9b4ddhf78x">
          <Paragraph data-id="element-1250" data-mp="3fhdcoonwru61">
            Cookies are small data files that are placed on your computer or
            mobile device when you visit a website. Cookies are widely used by
            website owners in order to make their websites work, or to work more
            efficiently, as well as to provide reporting information.
          </Paragraph>
          <Paragraph data-id="element-1251" data-mp="100yw2iebewyv">
            Cookies set by the website owner (in this case,
            findonlinecollege.com) are called "first-party cookies". Cookies set
            by parties other than the website owner are called "third-party
            cookies". Third-party cookies enable third-party features or
            functionality to be provided on or through the website (e.g.,
            advertising, interactive content, and analytics).
          </Paragraph>
        </NumberedSection>

        {/* Section 2 */}
        <NumberedSection id="section-2" number={2} title="Types of Cookies We Use" data-id="element-1252" data-mp="3damiosbkcqx">
          <Paragraph data-id="element-1253" data-mp="88g7nldwdndy">
            We use first-party and third-party cookies for several reasons. Some
            cookies are required for technical reasons in order for our website
            to operate, and we refer to these as "essential" or "strictly
            necessary" cookies. Other cookies also enable us to track and target
            the interests of our users to enhance the experience on our website.
          </Paragraph>

          <SubHeading data-id="element-1254" data-mp="3kuondy61xoq3">Strictly Necessary Cookies</SubHeading>
          <Paragraph data-id="element-1255" data-mp="3ks29lgskiczf">
            These cookies are essential for you to browse the website and use
            its features. Without these cookies, services like secure areas and
            basic functionality cannot be provided.
          </Paragraph>

          <SubHeading data-id="element-1256" data-mp="3psq8spqya04q">Performance and Analytics Cookies</SubHeading>
          <Paragraph data-id="element-1257" data-mp="7g1rtmgsce6r">
            These cookies collect information about how you use our website,
            like which pages you visited and which links you clicked on. None of
            this information can be used to identify you. It is all aggregated
            and, therefore, anonymized. Their sole purpose is to improve website
            functions.
          </Paragraph>

          <SubHeading data-id="element-1258" data-mp="25xlrkfuef0cy">Functional Cookies</SubHeading>
          <Paragraph data-id="element-1259" data-mp="25wssm11olf5d">
            These cookies allow our website to remember choices you make (such
            as your user name, language, or the region you are in) and provide
            enhanced, more personal features.
          </Paragraph>

          <SubHeading data-id="element-1260" data-mp="2klyphid1qta3">Advertising and Targeting Cookies</SubHeading>
          <Paragraph data-id="element-1261" data-mp="2kob4bmnicnix">
            These cookies are used to deliver advertisements more relevant to
            you and your interests. They are also used to limit the number of
            times you see an advertisement as well as help measure the
            effectiveness of the advertising campaign. They are usually placed
            by advertising networks with the website operator's permission. For
            example, we use the Meta Pixel to help us track conversions and
            deliver targeted advertisements.
          </Paragraph>
        </NumberedSection>

        {/* Section 3 */}
        <NumberedSection id="section-3" number={3} title="How Can You Control Cookies?" data-id="element-1262" data-mp="3dd07dr84j2xb">
          <Paragraph data-id="element-1263" data-mp="wopgkf9lx6yb">
            You have the right to decide whether to accept or reject cookies.
            You can exercise your cookie rights by setting your preferences in
            your web browser.
          </Paragraph>

          <SubHeading data-id="element-1264" data-mp="mxuw9gfx8hfi">Browser Controls</SubHeading>
          <Paragraph data-id="element-1265" data-mp="hzkzj1ejt8rs">
            You can set or amend your web browser controls to accept or refuse
            cookies. If you choose to reject cookies, you may still use our
            website though your access to some functionality and areas of our
            website may be restricted. As the means by which you can refuse
            cookies through your web browser controls vary from
            browser-to-browser, you should visit your browser's help menu for
            more information.
          </Paragraph>

          <SubHeading data-id="element-1266" data-mp="1ydjk0a101khd">Opting Out of Targeted Advertising</SubHeading>
          <Paragraph data-id="element-1267" data-mp="3su1h0clhlcgu">
            If you want to opt out of targeted advertising, you can visit the
            Digital Advertising Alliance (DAA) at{' '}
            <a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer" className="text-navy underline hover:text-crimson" data-id="element-1268" data-mp="1g5gxyhdmw9d">
              optout.aboutads.info
            </a>{' '}
            or the Network Advertising Initiative (NAI) at{' '}
            <a href="https://optout.networkadvertising.org" target="_blank" rel="noopener noreferrer" className="text-navy underline hover:text-crimson" data-id="element-1269" data-mp="fdak9qpphh0x">
              optout.networkadvertising.org
            </a>
            . You can also use the Do Not Track (DNT) settings in your browser.
          </Paragraph>
        </NumberedSection>

        {/* Section 4 */}
        <NumberedSection id="section-4" number={4} title="More Information" data-id="element-1270" data-mp="2v6srm6id7j9k">
          <Paragraph data-id="element-1271" data-mp="1d8o2p693cj3b">
            For more information about how we protect your privacy, please
            review our{' '}
            <Link to="/privacy-policy" className="text-navy underline hover:text-crimson" data-id="element-1272" data-mp="18acnsh3xvie8">
              Privacy Policy
            </Link>
            . If you are a California resident looking to exercise your rights
            regarding the sale or sharing of your personal information, please
            visit our{' '}
            <Link to="/do-not-sell-my-info" className="text-navy underline hover:text-crimson" data-id="element-1273" data-mp="pj21yed2lvt7">
              Do Not Sell My Info
            </Link>{' '}
            page.
          </Paragraph>
        </NumberedSection>

        {/* Section 5 */}
        <NumberedSection id="section-5" number={5} title="Contact Us" data-id="element-1274" data-mp="yhnjc66t34se">
          <Paragraph data-id="element-1275" data-mp="asmesu13gdse">
            If you have any questions about our use of cookies or other
            technologies, please contact us at:
          </Paragraph>
          <div className="mt-5 p-6 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-1276" data-mp="vnwef9e073dc">
            <p className="font-bold text-navy text-lg" data-id="element-1277" data-mp="10m6cpsv0yk36">13th Floor Media</p>
            <p className="text-gray-700 mt-2" data-id="element-1278" data-mp="10ok862tm9yb9">
              2870 S Federal Hwy., Suite 237, Deerfield Beach, FL 33441
            </p>
            <p className="text-gray-700 mt-2" data-id="element-1279" data-mp="19nnqh4kym9op">
              Email:{' '}
              <a href="mailto:contact@findonlinecollege.com" className="text-navy underline hover:text-crimson font-medium" data-id="element-1280" data-mp="19kru3korft2d">
                contact@findonlinecollege.com
              </a>
            </p>
          </div>
        </NumberedSection>
      </div>
    </main>;
}
/* ---------- Sub-components ---------- */
function NumberedSection({
  id,
  number,
  title,
  children
}: {
  id: string;
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return <section id={id} className="mt-16 pt-10 border-t border-gray-200 scroll-mt-8" data-id="element-1281" data-mp="22bjt0px7mvte">
      <div className="flex items-baseline gap-3 mb-6" data-id="element-1282" data-mp="2cg9pwhdrb9qr">
        <span className="text-crimson font-bold text-xl" data-id="element-1283" data-mp="2ch1ntyuttjk7">{number}.</span>
        <h2 className="text-2xl sm:text-3xl font-bold text-navy tracking-tight leading-tight" data-id="element-1284" data-mp="27fvrzs5vdg6g">
          {title}
        </h2>
      </div>
      <div className="space-y-5 leading-relaxed text-gray-700 text-base" data-id="element-1285" data-mp="22jn8y3ojx2u0">
        {children}
      </div>
    </section>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-1286" data-mp="2v8c8hy9yl03n">{children}</h3>;
}
function Paragraph({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-gray-700 leading-relaxed" data-id="element-1287" data-mp="2lfn2hix6gefp">{children}</p>;
}